import React, { createContext, useContext, useReducer, useCallback, useRef } from 'react';
import { projectReducer, initialProjectState, PROJECT_ACTIONS } from './projectReducer';

const ProjectContext = createContext();
const ProjectDispatchContext = createContext();
const UndoRedoContext = createContext();

const MAX_HISTORY = 50;
const SKIP_UNDO = new Set([PROJECT_ACTIONS.LOAD_PROJECT]);

export function ProjectProvider({ children }) {
  const [project, baseDispatch] = useReducer(projectReducer, initialProjectState);
  const projectRef = useRef(project);
  projectRef.current = project;
  const historyRef = useRef({ past: [], future: [] });

  // Stable dispatch that never changes reference
  const dispatch = useCallback((action) => {
    if (!SKIP_UNDO.has(action.type)) {
      const history = historyRef.current;
      history.past.push(projectRef.current);
      if (history.past.length > MAX_HISTORY) history.past.shift();
      history.future = [];
    }
    baseDispatch(action);
  }, []);

  const undo = useCallback(() => {
    const history = historyRef.current;
    if (history.past.length === 0) return;
    const prev = history.past.pop();
    history.future.push(projectRef.current);
    baseDispatch({ type: PROJECT_ACTIONS.LOAD_PROJECT, payload: prev });
  }, []);

  const redo = useCallback(() => {
    const history = historyRef.current;
    if (history.future.length === 0) return;
    const next = history.future.pop();
    history.past.push(projectRef.current);
    baseDispatch({ type: PROJECT_ACTIONS.LOAD_PROJECT, payload: next });
  }, []);

  return (
    <ProjectContext.Provider value={project}>
      <ProjectDispatchContext.Provider value={dispatch}>
        <UndoRedoContext.Provider value={{ undo, redo, historyRef }}>
          {children}
        </UndoRedoContext.Provider>
      </ProjectDispatchContext.Provider>
    </ProjectContext.Provider>
  );
}

export function useProject() {
  const context = useContext(ProjectContext);
  return context || null;
}

export function useProjectDispatch() {
  const context = useContext(ProjectDispatchContext);
  return context || null;
}

export function useUndoRedo() {
  const { undo, redo, historyRef } = useContext(UndoRedoContext);
  return {
    undo,
    redo,
    canUndo: historyRef.current.past.length > 0,
    canRedo: historyRef.current.future.length > 0,
  };
}

export function useLoadProject() {
  const dispatch = useProjectDispatch();

  const loadProject = useCallback(
    (projectData) => {
      if (dispatch && projectData) {
        dispatch({ type: PROJECT_ACTIONS.LOAD_PROJECT, payload: projectData });
      }
    },
    [dispatch]
  );

  return loadProject;
}
